 import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faMoneyBillWave, faSave, faTimes, faSpinner,
    faCheckCircle, faExclamationTriangle, faPhone, faEnvelope,
    faMapMarkerAlt, faWallet, faHistory, faSearch, faFilter,
    faCalendarAlt, faCreditCard, faUniversity, faEye,
    faReceipt, faInfoCircle, faArrowLeft, faArrowRight,
    faUser, faTag, faUsers, faPlus
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Helper Functions
// ==============================================
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
    }).format(amount || 0);
};

const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString('en-IN') : '';
};

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast }) => {
    if (!toast.show) return null;
    return (
        <div className={`toast-notification ${toast.type}`}>
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} />
                </div>
                <div className="toast-text">
                    <div className="toast-message">{toast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setToast({ show: false })}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Transaction Details Modal
// ==============================================
const TransactionDetailsModal = ({ show, onClose, transaction }) => {
    if (!show || !transaction) return null;
    
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content details-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3><FontAwesomeIcon icon={faReceipt} /> Transaction Details</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <div className="details-grid">
                        <div className="detail-row">
                            <span className="detail-label">Date:</span>
                            <span className="detail-value">{formatDate(transaction.transaction_date)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Type:</span>
                            <span className="detail-value type-payment">Payment Out</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Payment Source:</span>
                            <span className="detail-value">
                                {transaction.payment_source === 'owner' ? 'Owner Account' : `Partner: ${transaction.partner_name}`}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Reference:</span>
                            <span className="detail-value">{transaction.reference_no || '-'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Amount:</span>
                            <span className="detail-value amount debit">-{formatCurrency(transaction.amount)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Payment Method:</span>
                            <span className="detail-value">{transaction.payment_method || 'Cash'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Balance Before:</span>
                            <span className="detail-value">{formatCurrency(transaction.balance_before)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Balance After:</span>
                            <span className="detail-value">{formatCurrency(transaction.balance_after)}</span>
                        </div>
                        {transaction.notes && (
                            <div className="detail-row">
                                <span className="detail-label">Notes:</span>
                                <span className="detail-value">{transaction.notes}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Main Supplier Transactions Component
// ==============================================
const SupplierTransactions = () => {
    const { user, currentShop } = useAuth();
    
    // Shop ID
    const [shopId, setShopId] = useState(() => {
        if (currentShop?.shop_id) return currentShop.shop_id;
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });
    
    // State
    const [suppliersList, setSuppliersList] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    
    // Payment form state
    const [paymentSource, setPaymentSource] = useState('owner');
    const [partnersList, setPartnersList] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [notes, setNotes] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    
    // Tab state
    const [activeTab, setActiveTab] = useState('form');
    
    // Transactions history
    const [transactions, setTransactions] = useState([]);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    
    const currentLanguage = localStorage.getItem('ourzest_language') || 'en';
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    
    const showToast = (type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast({ show: false }), 3000);
    };
    
    // Load suppliers list
    const loadSuppliers = useCallback(async () => {
        if (!shopId) return;
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/supplier_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_all_suppliers_with_balance',
                    shop_id: parseInt(shopId),
                    username: user?.username
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setSuppliersList(data.data || []);
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
            showToast('error', 'Failed to load suppliers');
        } finally {
            setLoading(false);
        }
    }, [shopId, user?.username]);
    
    // Load partners list for payment source
    const loadPartners = useCallback(async () => {
        if (!shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/partners.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user?.username,
                    shop_id: parseInt(shopId)
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setPartnersList(data.data || []);
            }
        } catch (error) {
            console.error('Error loading partners:', error);
        }
    }, [shopId, user?.username]);
    
    // Load transactions for selected supplier
    const loadTransactions = useCallback(async (supplierId) => {
        if (!shopId || !supplierId) return;
        setLoadingTransactions(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/supplier_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_supplier_transactions',
                    supplier_id: parseInt(supplierId),
                    shop_id: parseInt(shopId),
                    username: user?.username,
                    limit: 100
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setTransactions(data.data || []);
            }
        } catch (error) {
            console.error('Error loading transactions:', error);
            showToast('error', 'Failed to load transactions');
        } finally {
            setLoadingTransactions(false);
        }
    }, [shopId, user?.username]);
    
    useEffect(() => {
        if (shopId && user?.username) {
            loadSuppliers();
            loadPartners();
        }
    }, [shopId, user?.username, loadSuppliers, loadPartners]);
    
    useEffect(() => {
        if (selectedSupplier) {
            loadTransactions(selectedSupplier.supplierid);
        }
    }, [selectedSupplier, loadTransactions]);
    
    const validateForm = () => {
        const newErrors = {};
        if (!selectedSupplier) newErrors.supplier = 'Please select a supplier';
        if (!amount || parseFloat(amount) <= 0) newErrors.amount = 'Amount must be greater than 0';
        if (paymentSource === 'partner' && !selectedPartner) newErrors.partner = 'Please select a partner';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const resetForm = () => {
        setAmount('');
        setReferenceNo('');
        setNotes('');
        setPaymentMethod('cash');
        setSelectedPartner(null);
        setErrors({});
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        
        setSaving(true);
        try {
            const username = user?.username || localStorage.getItem('username');
            
            const paymentData = {
                action: 'record_supplier_payment',
                supplier_id: selectedSupplier.supplierid,
                shop_id: parseInt(shopId),
                amount: parseFloat(amount),
                payment_date: paymentDate,
                username: username,
                notes: notes,
                reference_no: referenceNo,
                payment_method: paymentMethod,
                payment_source: paymentSource
            };
            
            if (paymentSource === 'partner' && selectedPartner) {
                paymentData.partner_id = selectedPartner.partner_id;
                paymentData.partner_name = selectedPartner.partner_name;
            }
            
            const response = await fetch('https://ourzestapp.online/oz_backend/supplier_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(paymentData)
            });
            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', `Payment of ${formatCurrency(amount)} recorded successfully`);
                resetForm();
                loadSuppliers();
                loadTransactions(selectedSupplier.supplierid);
                setActiveTab('transactions');
            } else {
                showToast('error', data.message || 'Failed to record payment');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };
    
    const handleViewTransaction = (transaction) => {
        setSelectedTransaction(transaction);
        setShowDetailsModal(true);
    };
    
    const filteredSuppliers = suppliersList.filter(supplier => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return supplier.supplier_name?.toLowerCase().includes(term) ||
               supplier.company?.toLowerCase().includes(term) ||
               supplier.phone?.includes(term);
    });
    
    const paymentMethods = [
        { value: 'cash', label: 'Cash', icon: faMoneyBillWave },
        { value: 'bank_transfer', label: 'Bank Transfer', icon: faUniversity },
        { value: 'digital_wallet', label: 'Digital Wallet', icon: faWallet },
        { value: 'cheque', label: 'Cheque', icon: faReceipt },
        { value: 'card', label: 'Card', icon: faCreditCard }
    ];
    
    return (
        <div className={`supplier-transactions-container ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} />
            
            <TransactionDetailsModal
                show={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                transaction={selectedTransaction}
            />
            
            {/* Header */}
            <div className="account-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faBuilding} />
                    </div>
                    <div className="header-text">
                        <h1>Supplier Payments</h1>
                        <p>Manage supplier payments from Owner or Partner accounts</p>
                        {currentShop && (
                            <div className="shop-badge">
                                <FontAwesomeIcon icon={faTag} /> {currentShop.shop_name}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Main Layout */}
            <div className="main-layout">
                {/* Left Sidebar - Suppliers List */}
                <div className={`suppliers-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                    <div className="sidebar-header">
                        <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                            <FontAwesomeIcon icon={sidebarCollapsed ? faArrowRight : faArrowLeft} />
                        </button>
                        {!sidebarCollapsed && (
                            <div className="sidebar-title">
                                <FontAwesomeIcon icon={faUsers} />
                                <span>Suppliers</span>
                            </div>
                        )}
                    </div>
                    
                    {!sidebarCollapsed && (
                        <>
                            <div className="sidebar-search">
                                <div className="search-input-wrapper">
                                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search suppliers..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            
                            <div className="suppliers-list">
                                {loading ? (
                                    <div className="loading-state"><FontAwesomeIcon icon={faSpinner} spin /> Loading...</div>
                                ) : filteredSuppliers.length === 0 ? (
                                    <div className="empty-state">
                                        <FontAwesomeIcon icon={faBuilding} />
                                        <p>No suppliers found</p>
                                    </div>
                                ) : (
                                    filteredSuppliers.map(supplier => (
                                        <div
                                            key={supplier.supplierid}
                                            className={`supplier-item ${selectedSupplier?.supplierid === supplier.supplierid ? 'active' : ''}`}
                                            onClick={() => setSelectedSupplier(supplier)}
                                        >
                                            <div className="supplier-info">
                                                <div className="supplier-name">
                                                    <FontAwesomeIcon icon={faUser} />
                                                    <span>{supplier.supplier_name}</span>
                                                </div>
                                                {supplier.company && (
                                                    <div className="supplier-company">{supplier.company}</div>
                                                )}
                                                {supplier.phone && (
                                                    <div className="supplier-phone">
                                                        <FontAwesomeIcon icon={faPhone} size="xs" /> {supplier.phone}
                                                    </div>
                                                )}
                                                <div className={`supplier-balance ${parseFloat(supplier.current_balance) > 0 ? 'positive' : ''}`}>
                                                    Balance: {formatCurrency(supplier.current_balance)}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>
                
                {/* Right Content */}
                <div className="main-content">
                    {selectedSupplier ? (
                        <>
                            {/* Summary Cards */}
                            <div className="summary-cards">
                                <div className="summary-card">
                                    <div className="card-icon"><FontAwesomeIcon icon={faUser} /></div>
                                    <div className="card-content">
                                        <div className="card-label">Supplier Name</div>
                                        <div className="card-value">{selectedSupplier.supplier_name}</div>
                                        {selectedSupplier.company && (
                                            <div className="card-subvalue">{selectedSupplier.company}</div>
                                        )}
                                    </div>
                                </div>
                                <div className={`summary-card balance ${parseFloat(selectedSupplier.current_balance) > 0 ? 'positive' : ''}`}>
                                    <div className="card-icon"><FontAwesomeIcon icon={faWallet} /></div>
                                    <div className="card-content">
                                        <div className="card-label">Current Balance</div>
                                        <div className="card-value">{formatCurrency(selectedSupplier.current_balance)}</div>
                                        <div className="card-subvalue">Total Paid: {formatCurrency(selectedSupplier.total_payments)}</div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Tabs */}
                            <div className="tabs-container">
                                <button className={`tab-btn ${activeTab === 'form' ? 'active' : ''}`} onClick={() => setActiveTab('form')}>
                                    <FontAwesomeIcon icon={faMoneyBillWave} /> Make Payment
                                </button>
                                <button className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`} onClick={() => setActiveTab('transactions')}>
                                    <FontAwesomeIcon icon={faHistory} /> Payment History ({transactions.length})
                                </button>
                            </div>
                            
                            {/* Form Tab */}
                            {activeTab === 'form' && (
                                <form onSubmit={handleSubmit} className="payment-form">
                                    {/* Payment Source Toggle */}
                                    <div className="payment-source-toggle">
                                        <label className="toggle-label">Payment Source:</label>
                                        <div className="toggle-buttons">
                                            <button
                                                type="button"
                                                className={`source-btn ${paymentSource === 'owner' ? 'active' : ''}`}
                                                onClick={() => setPaymentSource('owner')}
                                            >
                                                <FontAwesomeIcon icon={faWallet} /> Owner Account
                                            </button>
                                            <button
                                                type="button"
                                                className={`source-btn ${paymentSource === 'partner' ? 'active' : ''}`}
                                                onClick={() => setPaymentSource('partner')}
                                            >
                                                <FontAwesomeIcon icon={faBuilding} /> Partner Account
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Partner Selection */}
                                    {paymentSource === 'partner' && (
                                        <div className="form-group">
                                            <label>Select Partner <span className="required">*</span></label>
                                            <select
                                                value={selectedPartner?.partner_id || ''}
                                                onChange={(e) => {
                                                    const partner = partnersList.find(p => p.partner_id === parseInt(e.target.value));
                                                    setSelectedPartner(partner);
                                                }}
                                                className="form-input"
                                                required
                                            >
                                                <option value="">Select a partner...</option>
                                                {partnersList.map(partner => (
                                                    <option key={partner.partner_id} value={partner.partner_id}>
                                                        {partner.partner_name} {partner.business_name ? `(${partner.business_name})` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.partner && <small className="error">{errors.partner}</small>}
                                        </div>
                                    )}
                                    
                                    {/* Amount Field */}
                                    <div className="form-group">
                                        <label>Amount <span className="required">*</span></label>
                                        <div className="amount-input-wrapper">
                                            <span className="currency-symbol">$</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                className="form-input"
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>
                                        {errors.amount && <small className="error">{errors.amount}</small>}
                                    </div>
                                    
                                    {/* Date and Reference */}
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Payment Date</label>
                                            <input
                                                type="date"
                                                value={paymentDate}
                                                onChange={(e) => setPaymentDate(e.target.value)}
                                                className="form-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Reference No</label>
                                            <input
                                                type="text"
                                                placeholder="Invoice / Bill number"
                                                value={referenceNo}
                                                onChange={(e) => setReferenceNo(e.target.value)}
                                                className="form-input"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Payment Method */}
                                    <div className="form-group">
                                        <label>Payment Method</label>
                                        <div className="payment-methods-grid">
                                            {paymentMethods.map(method => (
                                                <button
                                                    key={method.value}
                                                    type="button"
                                                    className={`payment-method-btn ${paymentMethod === method.value ? 'active' : ''}`}
                                                    onClick={() => setPaymentMethod(method.value)}
                                                >
                                                    <FontAwesomeIcon icon={method.icon} />
                                                    <span>{method.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Notes */}
                                    <div className="form-group">
                                        <label>Notes</label>
                                        <textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            rows="3"
                                            className="form-input"
                                            placeholder="Additional notes..."
                                        />
                                    </div>
                                    
                                    {/* Submit Button */}
                                    <div className="form-actions">
                                        <button type="button" className="btn-secondary" onClick={resetForm}>
                                            <FontAwesomeIcon icon={faTimes} /> Clear
                                        </button>
                                        <button type="submit" className="btn-primary" disabled={saving}>
                                            {saving ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</>
                                            ) : (
                                                <><FontAwesomeIcon icon={faSave} /> Record Payment</>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                            
                            {/* Transactions Tab */}
                            {activeTab === 'transactions' && (
                                <div className="transactions-section">
                                    {loadingTransactions ? (
                                        <div className="loading-state"><FontAwesomeIcon icon={faSpinner} spin /> Loading transactions...</div>
                                    ) : transactions.length === 0 ? (
                                        <div className="empty-state">
                                            <FontAwesomeIcon icon={faHistory} />
                                            <p>No payment records found</p>
                                        </div>
                                    ) : (
                                        <div className="transactions-table-container">
                                            <table className="transactions-table">
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Payment Source</th>
                                                        <th>Amount</th>
                                                        <th>Payment Method</th>
                                                        <th>Reference</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {transactions.map(tx => (
                                                        <tr key={tx.id}>
                                                            <td>{formatDate(tx.transaction_date)}</td>
                                                            <td>
                                                                <span className={`source-badge ${tx.payment_source}`}>
                                                                    {tx.payment_source === 'owner' ? 'Owner Account' : tx.partner_name || 'Partner'}
                                                                </span>
                                                            </td>
                                                            <td className="debit">-{formatCurrency(tx.amount)}</td>
                                                            <td>
                                                                <span className="method-badge">{tx.payment_method || 'Cash'}</span>
                                                            </td>
                                                            <td>{tx.reference_no || '-'}</td>
                                                            <td>
                                                                <button 
                                                                    className="btn-icon view" 
                                                                    onClick={() => handleViewTransaction(tx)}
                                                                    title="View Details"
                                                                >
                                                                    <FontAwesomeIcon icon={faEye} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="no-selection">
                            <FontAwesomeIcon icon={faBuilding} className="empty-icon" />
                            <h3>Select a Supplier</h3>
                            <p>Select a supplier from the left sidebar to make payments</p>
                        </div>
                    )}
                </div>
            </div>
            
            <style jsx>{`
                .supplier-transactions-container {
                    display: flex;
                    flex-direction: column;
                    height: 100vh;
                    background: #f5f6fa;
                    overflow: hidden;
                }
                
                .account-header-modern {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    padding: 20px 24px;
                    color: white;
                    flex-shrink: 0;
                }
                
                .header-content {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }
                
                .header-icon {
                    width: 50px;
                    height: 50px;
                    background: rgba(255,255,255,0.2);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                }
                
                .header-text h1 {
                    margin: 0;
                    font-size: 22px;
                }
                
                .header-text p {
                    margin: 4px 0 0;
                    opacity: 0.9;
                    font-size: 13px;
                }
                
                .shop-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255,255,255,0.2);
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    margin-top: 8px;
                }
                
                .main-layout {
                    display: flex;
                    flex: 1;
                    overflow: hidden;
                }
                
                /* Sidebar Styles */
                .suppliers-sidebar {
                    width: 300px;
                    background: white;
                    border-right: 1px solid #e9ecef;
                    display: flex;
                    flex-direction: column;
                    transition: width 0.3s ease;
                    flex-shrink: 0;
                }
                
                .suppliers-sidebar.collapsed {
                    width: 50px;
                }
                
                .sidebar-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px;
                    border-bottom: 1px solid #e9ecef;
                }
                
                .sidebar-toggle {
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #666;
                    padding: 8px;
                    border-radius: 6px;
                }
                
                .sidebar-toggle:hover {
                    background: #f0f0f0;
                }
                
                .sidebar-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    color: #333;
                }
                
                .sidebar-search {
                    padding: 12px;
                    border-bottom: 1px solid #e9ecef;
                }
                
                .search-input-wrapper {
                    position: relative;
                }
                
                .search-input-wrapper .search-icon {
                    position: absolute;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #999;
                    font-size: 12px;
                }
                
                .sidebar-search input {
                    width: 100%;
                    padding: 8px 8px 8px 32px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                    outline: none;
                }
                
                .suppliers-list {
                    flex: 1;
                    overflow-y: auto;
                }
                
                .supplier-item {
                    padding: 12px;
                    cursor: pointer;
                    border-bottom: 1px solid #f0f0f0;
                    transition: background 0.2s;
                }
                
                .supplier-item:hover {
                    background: #f8f9fa;
                }
                
                .supplier-item.active {
                    background: #ede9fe;
                    border-left: 3px solid #667eea;
                }
                
                .supplier-info {
                    flex: 1;
                }
                
                .supplier-name {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 500;
                    font-size: 14px;
                    margin-bottom: 4px;
                }
                
                .supplier-company {
                    font-size: 11px;
                    color: #888;
                    margin-bottom: 4px;
                }
                
                .supplier-phone {
                    font-size: 11px;
                    color: #888;
                    margin-bottom: 4px;
                }
                
                .supplier-balance {
                    font-size: 12px;
                    color: #666;
                    margin-top: 6px;
                }
                
                .supplier-balance.positive {
                    color: #dc3545;
                }
                
                /* Main Content */
                .main-content {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px;
                }
                
                /* Summary Cards */
                .summary-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 16px;
                    margin-bottom: 24px;
                }
                
                .summary-card {
                    background: white;
                    border-radius: 10px;
                    padding: 16px;
                    display: flex;
                    gap: 14px;
                    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
                }
                
                .summary-card.balance.positive {
                    border-top: 3px solid #dc3545;
                }
                
                .card-icon {
                    width: 45px;
                    height: 45px;
                    background: #f0eefe;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #667eea;
                    font-size: 20px;
                }
                
                .card-content {
                    flex: 1;
                }
                
                .card-label {
                    font-size: 11px;
                    color: #888;
                    text-transform: uppercase;
                    margin-bottom: 4px;
                }
                
                .card-value {
                    font-size: 18px;
                    font-weight: 700;
                    color: #1a1a2e;
                }
                
                .card-subvalue {
                    font-size: 11px;
                    color: #888;
                    margin-top: 4px;
                }
                
                /* Tabs */
                .tabs-container {
                    display: flex;
                    gap: 4px;
                    border-bottom: 2px solid #e9ecef;
                    margin-bottom: 20px;
                }
                
                .tab-btn {
                    padding: 10px 20px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-size: 14px;
                    color: #888;
                    border-bottom: 2px solid transparent;
                    margin-bottom: -2px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                
                .tab-btn.active {
                    color: #667eea;
                    border-bottom-color: #667eea;
                    font-weight: 600;
                }
                
                /* Payment Form */
                .payment-form {
                    background: white;
                    border-radius: 12px;
                    padding: 24px;
                    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
                }
                
                .payment-source-toggle {
                    margin-bottom: 24px;
                }
                
                .toggle-label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 500;
                    font-size: 13px;
                    color: #555;
                }
                
                .toggle-buttons {
                    display: flex;
                    gap: 12px;
                }
                
                .source-btn {
                    flex: 1;
                    padding: 10px;
                    border: 1px solid #ddd;
                    background: white;
                    border-radius: 8px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }
                
                .source-btn.active {
                    border-color: #28a745;
                    background: #e8f5e9;
                    color: #28a745;
                }
                
                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                    margin-bottom: 16px;
                }
                
                .form-group {
                    margin-bottom: 16px;
                }
                
                .form-group label {
                    display: block;
                    margin-bottom: 6px;
                    font-weight: 500;
                    font-size: 13px;
                    color: #555;
                }
                
                .required {
                    color: #dc3545;
                }
                
                .form-input {
                    width: 100%;
                    padding: 10px 12px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 14px;
                    box-sizing: border-box;
                }
                
                .form-input:focus {
                    outline: none;
                    border-color: #667eea;
                }
                
                .amount-input-wrapper {
                    position: relative;
                }
                
                .currency-symbol {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #999;
                }
                
                .amount-input-wrapper input {
                    padding-left: 30px;
                }
                
                .error {
                    color: #dc3545;
                    font-size: 11px;
                    margin-top: 4px;
                    display: block;
                }
                
                .payment-methods-grid {
                    display: flex;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                
                .payment-method-btn {
                    padding: 8px 16px;
                    border: 1px solid #ddd;
                    background: white;
                    border-radius: 20px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                }
                
                .payment-method-btn.active {
                    border-color: #667eea;
                    background: #ede9fe;
                    color: #667eea;
                }
                
                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    margin-top: 24px;
                    padding-top: 20px;
                    border-top: 1px solid #e9ecef;
                }
                
                .btn-primary {
                    padding: 10px 24px;
                    background: #28a745;
                    border: none;
                    border-radius: 8px;
                    color: white;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                
                .btn-primary:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .btn-secondary {
                    padding: 10px 24px;
                    background: #f5f5f5;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                
                /* Transactions Table */
                .transactions-section {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                }
                
                .transactions-table-container {
                    overflow-x: auto;
                }
                
                .transactions-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 13px;
                }
                
                .transactions-table th,
                .transactions-table td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #f0f0f0;
                }
                
                .transactions-table th {
                    background: #f8f9fa;
                    font-weight: 600;
                }
                
                .debit {
                    color: #dc3545;
                    font-weight: 600;
                }
                
                .source-badge {
                    display: inline-block;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 600;
                }
                
                .source-badge.owner {
                    background: #e8f5e9;
                    color: #28a745;
                }
                
                .source-badge.partner {
                    background: #e3f2fd;
                    color: #2196f3;
                }
                
                .method-badge {
                    display: inline-block;
                    padding: 3px 10px;
                    background: #f0f0f0;
                    border-radius: 12px;
                    font-size: 11px;
                    color: #666;
                    text-transform: capitalize;
                }
                
                .btn-icon {
                    background: none;
                    border: none;
                    padding: 6px;
                    cursor: pointer;
                    border-radius: 4px;
                    color: #999;
                }
                
                .btn-icon.view:hover {
                    background: #e8f5e9;
                    color: #28a745;
                }
                
                .no-selection {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    text-align: center;
                    color: #999;
                }
                
                .empty-icon {
                    font-size: 64px;
                    margin-bottom: 20px;
                    color: #ddd;
                }
                
                .loading-state, .empty-state {
                    text-align: center;
                    padding: 40px;
                    color: #999;
                }
                
                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }
                
                .modal-content {
                    background: white;
                    border-radius: 12px;
                    width: 90%;
                    max-width: 550px;
                    max-height: 85vh;
                    overflow-y: auto;
                }
                
                .details-modal {
                    max-width: 550px;
                }
                
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 20px;
                    border-bottom: 1px solid #e9ecef;
                }
                
                .modal-header h3 {
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                
                .modal-close {
                    background: none;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    color: #999;
                }
                
                .modal-body {
                    padding: 20px;
                }
                
                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    padding: 16px 20px;
                    border-top: 1px solid #e9ecef;
                }
                
                .details-grid {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }
                
                .detail-row {
                    display: flex;
                    padding: 8px 0;
                    border-bottom: 1px solid #f0f0f0;
                }
                
                .detail-label {
                    width: 130px;
                    font-weight: 600;
                    color: #555;
                    font-size: 13px;
                }
                
                .detail-value {
                    flex: 1;
                    color: #333;
                    font-size: 13px;
                }
                
                .detail-value.amount.debit {
                    color: #dc3545;
                    font-weight: 600;
                }
                
                /* Toast */
                .toast-notification {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 9999;
                    animation: slideIn 0.3s ease;
                }
                
                @keyframes slideIn {
                    from {
                        transform: translateX(110%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                
                .toast-content {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 14px 20px;
                    color: white;
                    border-radius: 10px;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
                }
                
                .toast-notification.success .toast-content {
                    background: #28a745;
                }
                
                .toast-notification.error .toast-content {
                    background: #dc3545;
                }
                
                .toast-close {
                    background: none;
                    border: none;
                    color: white;
                    cursor: pointer;
                }
                
                /* RTL */
                .rtl .text-right {
                    text-align: left !important;
                }
                
                .rtl .search-input-wrapper .search-icon {
                    left: auto;
                    right: 10px;
                }
                
                .rtl .sidebar-search input {
                    padding: 8px 32px 8px 8px;
                }
                
                .rtl .amount-input-wrapper input {
                    padding-left: 12px;
                    padding-right: 30px;
                }
                
                .rtl .currency-symbol {
                    left: auto;
                    right: 12px;
                }
                
                .rtl .detail-label {
                    text-align: right;
                }
            `}</style>
        </div>
    );
};

export default SupplierTransactions;